import { useEffect } from "react";
import {
  getCurrentLoyaltyTier,
  sortLoyaltyTiers,
} from "../../../utils/loyalty/tiers";
import { getCurrentTokenCustomerId } from "../../../utils/help/user/user";
import { useModalHook } from "../useModal";
import { useAppSelector } from "../useReduxHooks";
import { selectGuestTiersEnabled } from "../../redux/selectors/loyaltyServiceSelectors";

type TierSnapshot = {
  tierId: string;
  tierOrder: number;
  remainingUnitsToStay: number;
};

export const useLoyaltyTierChangePopup = () => {
  const { handleModal } = useModalHook();
  const enabled = useAppSelector(selectGuestTiersEnabled);
  const customerInfo = useAppSelector(
    (state) => state.loyaltyCustomerInfo.data
  );
  const tierSystem = useAppSelector((state) => state.loyaltyTierSystem.data);
  const modalOpen = useAppSelector((state) => state.modal.modal?.isOpen);

  useEffect(() => {
    const progress = customerInfo?.tierProgress;
    const currentTier = getCurrentLoyaltyTier(customerInfo, tierSystem);
    const customerId = getCurrentTokenCustomerId();
    if (
      !enabled ||
      !progress ||
      !currentTier ||
      !tierSystem ||
      !customerId ||
      modalOpen
    ) {
      return;
    }

    const storageKey = `loyalty-tier-snapshot:${tierSystem.tierSystemUuid}:${customerId}`;
    const snapshot: TierSnapshot = {
      tierId: currentTier.tierId,
      tierOrder: currentTier.tierOrder,
      remainingUnitsToStay: progress.remainingUnitsToStay,
    };
    let savedSnapshot: string | null;
    try {
      savedSnapshot = window.localStorage.getItem(storageKey);
      window.localStorage.setItem(storageKey, JSON.stringify(snapshot));
    } catch {
      return;
    }
    if (!savedSnapshot) {
      return;
    }

    let previous: TierSnapshot;
    try {
      previous = JSON.parse(savedSnapshot) as TierSnapshot;
    } catch {
      return;
    }

    const tiers = sortLoyaltyTiers(tierSystem.tiers);
    const isHighestTier =
      currentTier.tierOrder === tiers[tiers.length - 1]?.tierOrder;
    const kind =
      currentTier.tierOrder > previous.tierOrder
        ? "upgrade"
        : currentTier.tierOrder < previous.tierOrder
        ? "downgrade"
        : isHighestTier &&
          previous.remainingUnitsToStay > 0 &&
          progress.remainingUnitsToStay === 0
        ? "steady"
        : null;

    if (kind) {
      handleModal(true, "LoyaltyTierChange/LoyaltyTierChange", {
        componentProps: { kind, tierId: currentTier.tierId },
      });
    }
  }, [
    enabled,
    customerInfo?.tierProgress?.currentTierId,
    customerInfo?.tierProgress?.remainingUnitsToStay,
    tierSystem?.tierSystemUuid,
    modalOpen,
  ]);
};
